/**
 * Canonical input normalisation for *stored* user data — the single place
 * that decides how a name / email / phone / free-text note is shaped before
 * it hits Firestore.
 *
 * Distinct from `text-search.ts`'s `normalizeSearch()`, which is a *lossy*
 * transform (strips diacritics, lowercases) used only to MATCH a local list.
 * These functions are *meaning-preserving*: they never strip accents and
 * never re-case a human name — they only remove ambiguity that has no
 * semantic value (stray whitespace, Unicode form, identifier casing).
 *
 * Best-practice rules encoded here (see docs/development/best-practices.md):
 *  - Trim + collapse runs of internal whitespace; Unicode-normalise (NFC) so
 *    "José" (precomposed) and "José" (e + combining accent) compare equal.
 *  - NEVER auto-capitalise a person/club name. Title-casing mangles real
 *    Spanish names — "María de la O", "de la Fuente", "y Gómez" — and
 *    apostrophe/particle names ("O'Brien", "van der Berg"). The owner types
 *    the casing they want; we preserve it.
 *  - Identifiers (email) ARE lowercased — `Jose@X.com` and `jose@x.com` must
 *    collide for dedupe (`findByEmail`, staff invite, booking upsert).
 *  - Phone is canonicalised to `+`?digits so "+34 600 11 22 33" and
 *    "+34600112233" dedupe to the same `where('phone','==',…)` key.
 *
 * All four are idempotent: `f(f(x)) === f(x)`.
 */

/** Collapse every run of Unicode whitespace to a single ASCII space. */
function collapseWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ')
}

/**
 * Person / club / group name. Trim ends, collapse inner whitespace, NFC.
 * Casing and accents are preserved exactly as entered.
 */
export function normalizeName(s: string): string {
  return collapseWhitespace(s.normalize('NFC').trim())
}

/**
 * Email identifier. Trim + lowercase + NFC. Lowercasing the local part is
 * technically lossy per RFC 5321 but is universal practice and is what every
 * lookup path in this codebase already assumes.
 */
export function normalizeEmail(s: string): string {
  return s.normalize('NFC').trim().toLowerCase()
}

/**
 * Phone. Keep a single leading `+` (international prefix) and digits; drop
 * spaces, dashes, dots, parentheses. No country inference — there is no
 * locale context at this layer. Empty/whitespace-only → ''.
 */
export function normalizePhone(s: string): string {
  const trimmed = s.trim()
  if (trimmed === '') return ''
  // A `+` anywhere signals international intent — handles "+34…", "(+34)…",
  // "00 34" stays national. Collapse to a single leading `+` + digits.
  const hasPlus = trimmed.includes('+')
  const digits = trimmed.replace(/\D/g, '')
  if (digits === '') return ''
  return hasPlus ? `+${digits}` : digits
}

/**
 * Free-text note. Trim leading/trailing whitespace only — internal newlines
 * and intentional formatting are the user's, leave them be. NFC for stable
 * storage/diffing.
 */
export function normalizeNotes(s: string): string {
  return s.normalize('NFC').trim()
}

/**
 * Tag identifier (CRM player tags, etc.). UNLIKE names, tags ARE
 * lowercased — "VIP" and "vip" must dedupe to the same key. Trim, NFC,
 * lowercase, collapse inner whitespace. Returns '' for trivially short
 * strings (< 2 chars after normalisation) so callers can filter them
 * out via `.filter(Boolean)` without an explicit length check.
 *
 * Idempotent. Used by `useSetPlayerTags`, `useCreatePlayer`, and the
 * `CrmPlayerSchema.tags` `.transform()` so legacy un-normalised docs
 * self-heal on every read.
 */
export function normalizeTag(s: string): string {
  const t = collapseWhitespace(s.normalize('NFC').trim().toLowerCase())
  return t.length < 2 ? '' : t
}
